// src/contexts/SecurityContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import KeyGuardModal from '../KeyGuardModal'; 
import SessionExpiredModal from '../widgets/SessionExpiredModal'; 
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../services/config';

const SecurityContext = createContext();

export const useSecurity = () => useContext(SecurityContext);

export const SecurityProvider = ({ children }) => {
  const { logout, isAuthenticated } = useAuth(); // Assuming useAuth exposes isAuthenticated
  
  const [isKeyModalOpen, setKeyModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('verify'); 
  const [pendingAction, setPendingAction] = useState(null); 
  const [isSessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  // --- 1. SECURE FETCH WRAPPER ---
  const secureFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    const headers = { 
      'Content-Type': 'application/json',
      ...options.headers, 
      'Authorization': `Bearer ${token}` 
    };

    try {
      const response = await fetch(fullUrl, { ...options, headers });

      // INTERCEPTOR: Check for Revoked Session
      if (response.status === 401) {
        const data = await response.clone().json().catch(() => ({}));
        if (data.code === 'SESSION_REVOKED') {
          setSessionExpiredOpen(true); 
          return response; 
        }
      }

      // INTERCEPTOR: Check for Key Verification
      if (response.status === 403) {
        const data = await response.clone().json().catch(() => ({}));
        if (data.code === 'KEY_VERIFICATION_REQUIRED') {
          setAuthMode('verify');
          setKeyModalOpen(true);
          return new Promise((resolve, reject) => {
            setPendingAction(() => async () => {
              try {
                const retryResponse = await fetch(fullUrl, { ...options, headers });
                resolve(retryResponse);
              } catch (err) { reject(err); }
            });
          });
        }
      }
      
      return response;
    } catch (error) {
      console.error("Secure fetch error:", error);
      throw error;
    }
  }, []);

  // --- 2. HEARTBEAT  ---
  // Checks session validity every 30 seconds if the user is logged in
  useEffect(() => {
    if (!isAuthenticated) return; // Don't check if not logged in

    const checkSessionStatus = async () => {
      try {
        await secureFetch('/auth/ping'); 
      } catch (e) {
        // secureFetch handles the error internally
      }
    };

    const intervalId = setInterval(checkSessionStatus, 30000); // 30 seconds
    return () => clearInterval(intervalId);
  }, [isAuthenticated, secureFetch]);


  // --- 3. TIMEOUT LOGIC FOR KEY MODAL ---
  useEffect(() => {
    let timeout;
    if (isKeyModalOpen && authMode === 'verify') {
      timeout = setTimeout(() => {
        setKeyModalOpen(false);
        setSessionExpiredOpen(true); 
      }, 5 * 60 * 1000); 
    }
    return () => clearTimeout(timeout);
  }, [isKeyModalOpen, authMode]);

  const openKeySetup = () => { setAuthMode('register'); setKeyModalOpen(true); };
  
  const handleAuthSuccess = () => { 
    if (pendingAction) { pendingAction(); setPendingAction(null); } 
    setKeyModalOpen(false); 
  };

  const handleSessionExpiredConfirm = () => { 
    setSessionExpiredOpen(false); 
    logout(true); 
  };

  return (
    <SecurityContext.Provider value={{ secureFetch, openKeySetup }}>
      {children}
      
      <KeyGuardModal 
        isOpen={isKeyModalOpen} 
        onClose={() => setKeyModalOpen(false)} 
        onSuccess={handleAuthSuccess} 
        mode={authMode} 
      />
      
      {/* This modal sits at the root level, so it appears over everything */}
      <SessionExpiredModal 
        isOpen={isSessionExpiredOpen} 
        onConfirm={handleSessionExpiredConfirm} 
      />
      
    </SecurityContext.Provider>
  );
};
